// handleDownloadTestHistoryExcel.js - ExcelJS version
/**
 * - Summary sheet ("Test Results Overview")
 * - One sheet per test result (detailed)
 *
 * Expects each "test" to be the detailed object from:
 * GET /get-regression-test-result?id=<id>
 *
 * Behavior:overa
 * - Event listing excludes non-validated events (truth.status===404 || beaconData.status===404)
 */

import ExcelJS from 'exceljs';
import { getBeaconStatus } from './getBeaconStatus';

// ----------------- Styles (ExcelJS format) -----------------
const styles = {
  header: {
    font: { bold: true, color: { argb: 'FFFFFFFF' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF211747' } },
    alignment: { horizontal: 'center', vertical: 'middle' },
  },
  title: {
    font: { bold: true, size: 14 },
    alignment: { horizontal: 'left' },
  },
  label: {
    font: { bold: true },
    alignment: { horizontal: 'left' },
  },
  pass: {
    font: { color: { argb: 'FF2E7D32' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5E9' } },
  },
  fail: {
    font: { color: { argb: 'FFC62828' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFEBEE' } },
  },
  missing: {
    font: { color: { argb: 'FFE65100' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF3E0' } },
  },
  extra: {
    font: { color: { argb: 'FF1565C0' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE3F2FD' } },
  },
  notValidated: {
    font: { color: { argb: 'FF757575' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } },
  },
  staticField: {
    font: { bold: true },
  },
  link: {
    font: { color: { argb: 'FF2B67B9' }, underline: true },
  },
  eventDetailsHeader: {
    font: { bold: true, size: 13 },
    alignment: { horizontal: 'left' },
  },
  eventTitle: {
    font: { bold: true, size: 11 },
    alignment: { horizontal: 'left' },
  },
};

// Helper to apply style to a cell
function applyStyle(cell, style) {
  if (!style) return;
  if (style.font) cell.font = style.font;
  if (style.fill) cell.fill = style.fill;
  if (style.alignment) cell.alignment = style.alignment;
}

const safeGet = (obj, path, fallback = undefined) => {
  try {
    if (!obj) return fallback;
    const parts = Array.isArray(path) ? path : path.split('.');
    let cur = obj;
    for (const p of parts) {
      cur = cur?.[p];
      if (cur === undefined) return fallback;
    }
    return cur;
  } catch {
    return fallback;
  }
};

function sanitizeSheetName(name) {
  if (!name) return 'Sheet';
  return name
    .replace(/[\\/*?:[\]]/g, '_')
    .trim()
    .slice(0, 28);
}

function uniqueSheetName(existing, base, idx) {
  let name = sanitizeSheetName(base || `Test_${idx}`);
  if (!existing.has(name)) {
    existing.add(name);
    return name;
  }
  let count = 2;
  while (existing.has(`${name}_${count}`) && count < 999) count++;
  const final = `${name}_${count}`.slice(0, 31);
  existing.add(final);
  return final;
}

function formatTimestamp(ts) {
  if (!ts && ts !== 0) return '';
  try {
    if (typeof ts === 'number') return new Date(ts).toLocaleString();
    const numeric = Number(ts);
    if (!Number.isNaN(numeric)) return new Date(numeric).toLocaleString();
    return new Date(ts).toLocaleString();
  } catch {
    return String(ts);
  }
}

function determineEventStatus(ev) {
  const status = getBeaconStatus(ev);

  const map = {
    PASS: {
      text: 'Pass',
      style: styles.pass,
    },
    FAIL: {
      text: 'Fail',
      style: styles.fail,
    },
    MISSING: {
      text: 'Missing',
      style: styles.missing,
    },
    NOT_VALIDATED: {
      text: 'Not Validated',
      style: styles.notValidated,
      nonValidated: true,
    },
    NOT_PRESENT_IN_APP: {
      text: 'Not Present In App',
      style: styles.notValidated,
    },
  };

  return (
    map[status] || {
      text: status,
      style: {},
    }
  );
}

function buildAttributeRows(ev) {
  const rows = [];
  const flowComp = ev.flowComparison || ev.flow_comparison || safeGet(ev, ['flowComparison'], null);
  const modifiedRows = ev.modifiedRows || ev.modified_rows || {};

  if (Array.isArray(flowComp) && flowComp.length) {
    const sorted = [...flowComp].sort((a, b) => {
      const aExtra = (a.passFail || a.status || '').toString().toLowerCase() === 'extra';
      const bExtra = (b.passFail || b.status || '').toString().toLowerCase() === 'extra';
      if (aExtra !== bExtra) return aExtra ? 1 : -1;
      const aPr = (a.variableName || a.variable_name || '').includes('custom_attributes') ? 0 : 1;
      const bPr = (b.variableName || b.variable_name || '').includes('custom_attributes') ? 0 : 1;
      if (aPr !== bPr) return aPr - bPr;
      const aName = (a.variableName || a.variable_name || '').toString();
      const bName = (b.variableName || b.variable_name || '').toString();
      return aName.localeCompare(bName);
    });

    for (const r of sorted) {
      const varName = r.variableName ?? r.variable_name ?? r.key ?? '';
      const userOverride = modifiedRows[varName];
      const rawStatus = userOverride || (r.passFail || r.status || r.result || '').toString();
      const st = rawStatus.trim().toLowerCase();
      let style = {};
      let displayStatus = rawStatus || '';

      if (st === 'pass' || st === 'passed' || st === 'ok') style = styles.pass;
      else if (st === 'fail' || st === 'failed' || st === 'mismatch') style = styles.fail;
      else if (st === 'missing') style = styles.missing;
      else if (st === 'extra') style = styles.extra;

      const expected = r.expectedValue ?? r.expected_value ?? r.expected ?? r.expected_val ?? '';
      const actual = r.actualValue ?? r.actual_value ?? r.actual ?? r.actual_val ?? '';

      rows.push({
        varName,
        expected: expected ?? '',
        actual: actual ?? '',
        displayStatus,
        style,
        fieldType: r.fieldType ?? r.type ?? '',
        isStatic: displayStatus !== 'Extra',
      });
    }
    return rows;
  }

  const truthData = safeGet(ev, ['truth', 'data'], {}) || {};
  const beaconData = safeGet(ev, ['beaconData', 'data'], {}) || {};
  const keys = new Set([
    ...Object.keys(truthData || {}).filter((k) => k !== 'market'),
    ...Object.keys(beaconData || {}).filter((k) => k !== 'market'),
  ]);

  Array.from(keys)
    .sort()
    .forEach((key) => {
      const expObj = truthData[key];
      const actObj = beaconData[key];
      const expected = expObj && expObj.value !== undefined ? expObj.value : (expObj ?? '');
      const actual = actObj && actObj.value !== undefined ? actObj.value : (actObj ?? '');
      let pf = 'Pass';
      if ((expected === '' || expected === null || expected === undefined) && actual !== '')
        pf = 'Extra';
      else if ((actual === '' || actual === null || actual === undefined) && expected !== '')
        pf = 'Missing';
      else if (String(expected) !== String(actual)) pf = 'Fail';

      const style =
        pf === 'Pass'
          ? styles.pass
          : pf === 'Fail'
            ? styles.fail
            : pf === 'Missing'
              ? styles.missing
              : styles.extra;

      rows.push({
        varName: key,
        expected,
        actual,
        displayStatus: pf,
        style,
        fieldType: '',
        isStatic: pf !== 'Extra',
      });
    });

  return rows;
}

export async function handleDownloadTestHistoryExcel(testData = [], options = {}) {
  if (!Array.isArray(testData)) {
    throw new Error('Expected testData array');
  }

  const { regressionTag = '' } = options;

  const wb = new ExcelJS.Workbook();
  const now = new Date();
  const regressionId = regressionTag || testData[0]?.regression_test_id || 'unknown';

  // SUMMARY sheet
  const summarySheet = wb.addWorksheet('Test Results Overview');

  // Column widths
  summarySheet.columns = [
    { width: 15 }, // link
    { width: 25 }, // flow
    { width: 25 }, // test case
    { width: 15 }, // market
    { width: 20 }, // device
    { width: 12 }, // device type
    { width: 22 }, // executed at
    { width: 28 }, // user
    { width: 12 }, // pass rate
    { width: 8 },
    { width: 8 },
    { width: 8 },
    { width: 8 },
    { width: 28 }, // notes
  ];

  // Title
  let row = summarySheet.addRow(['Test History Export']);
  applyStyle(row.getCell(1), styles.title);
  summarySheet.addRow([]);

  row = summarySheet.addRow(['Export Information']);
  applyStyle(row.getCell(1), styles.label);
  summarySheet.addRow(['Regression Test ID:', regressionId]);
  summarySheet.addRow(['Export Date:', now.toLocaleString()]);
  summarySheet.addRow(['Total Results:', testData.length]);
  summarySheet.addRow([]);

  row = summarySheet.addRow(['Overall Statistics']);
  applyStyle(row.getCell(1), styles.label);

  // Compute overall stats
  let totalEvents = 0;
  let passedEvents = 0;
  let failedEvents = 0;
  let missingEvents = 0;
  let nonValidatedEvents = 0;
  let notPresentInAppEvents = 0;

  testData.forEach((t) => {
    const s = typeof t.summary === 'string' ? JSON.parse(t.summary) : t.summary || {};
    console.log('FULL TEST', t);
    console.log('Overall Stats', t.id, t.not_present_in_app, t.non_validated_events);
    const tTotal = s.total_events ?? s.totalEvents ?? t.total_events ?? t.totalEvents ?? 0;
    const tPassed = s.passed ?? s.passed_events ?? t.passed_events ?? t.passed ?? 0;
    const tFailed = s.failed ?? s.failed_events ?? t.failed_events ?? t.failed ?? 0;
    const tMissing = s.missing ?? s.missing_events ?? t.missing_events ?? t.missing ?? 0;
    const tNonValidated = s.non_validated ?? s.non_validated_events ?? t.non_validated_events ?? 0;
    const tNotPresentInApp = s.not_present_in_app ?? t.not_present_in_app ?? 0;
    totalEvents += Number(tTotal || 0);
    passedEvents += Number(tPassed || 0);
    failedEvents += Number(tFailed || 0);
    missingEvents += Number(tMissing || 0);
    nonValidatedEvents += Number(tNonValidated || 0);
    notPresentInAppEvents += Number(tNotPresentInApp || 0);
  });

  const avgPassRate = totalEvents ? ((passedEvents / totalEvents) * 100).toFixed(1) : '0.0';

  summarySheet.addRow(['Total Events:', totalEvents]);
  summarySheet.addRow(['Passed Events:', passedEvents]);
  summarySheet.addRow(['Failed Events:', failedEvents]);
  summarySheet.addRow(['Missing Events:', missingEvents]);
  summarySheet.addRow(['Average Pass Rate:', `${avgPassRate}%`]);
  summarySheet.addRow(['Non-Validated Events:', nonValidatedEvents]);
  summarySheet.addRow(['Not Present In App:', notPresentInAppEvents]);
  summarySheet.addRow([]);

  // Table header row
  const headerRow = summarySheet.addRow([
    'Sheet Link',
    'Flow Name',
    'Test Case',
    'Market',
    'Device',
    'Device Type',
    'Executed At',
    'User',
    'Pass Rate',
    'Total',
    'Passed',
    'Failed',
    'Missing',
    'Notes',
  ]);
  headerRow.eachCell((cell) => applyStyle(cell, styles.header));

  // Unique sheet names
  const usedNames = new Set(['Test Results Overview']);
  const sheetNames = testData.map((t, idx) => {
    const base = t.test_case_name ?? t.test_case_id ?? t.testCase ?? `Test_${idx + 1}`;
    const tsPart = (t.timestamp_start || t.created_at || t.executed_at || '')
      .toString()
      .replace(/[^0-9]/g, '')
      .slice(-6);
    const candidate = `${base}_${tsPart}`.slice(0, 28);
    return uniqueSheetName(usedNames, candidate || base, idx + 1);
  });

  // Summary table rows with hyperlinks
  testData.forEach((t, idx) => {
    const sheetName = sheetNames[idx];
    const flowName = t.flow_name ?? t.flowName ?? '';
    const testCase = t.test_case_name ?? t.test_case_id ?? t.testCase ?? '';
    const market = t.market ?? t.market_name ?? '';
    const device = t.device_name ?? t.device ?? t.device_id ?? '';
    const deviceType = t.device_type ?? t.deviceType ?? '';
    const executedAt = t.created_at ?? t.executed_at ?? formatTimestamp(t.executed_at) ?? '';
    const user = t.user_email ?? t.tester_email ?? '';
    const passRate = t.summary?.pass_rate ?? t.pass_rate ?? t.passRate ?? 0;
    const total = t.summary?.total_events ?? t.total_events ?? t.totalEvents ?? 0;
    const passed = t.summary?.passed ?? t.passed_events ?? t.passed ?? 0;
    const failed = t.summary?.failed ?? t.failed_events ?? t.failed ?? 0;
    const missing = t.summary?.missing ?? t.missing_events ?? t.missing ?? 0;
    const notes = t.notes ?? '';

    const dataRow = summarySheet.addRow([
      'View Details →',
      flowName,
      testCase,
      market,
      device,
      deviceType,
      executedAt,
      user,
      `${passRate}%`,
      total,
      passed,
      failed,
      missing,
      notes,
    ]);

    // Add hyperlink to sheet
    const linkCell = dataRow.getCell(1);
    linkCell.value = {
      text: 'View Details →',
      hyperlink: `#'${sheetName}'!A1`,
    };
    applyStyle(linkCell, styles.link);
  });

  // ---------- Per-test sheets ----------
  testData.forEach((test, idx) => {
    const sheetName = sheetNames[idx];
    const ws = wb.addWorksheet(sheetName);

    // Column widths
    ws.columns = [
      { width: 40 },
      { width: 40 },
      { width: 30 },
      { width: 20 },
      { width: 12 },
      { width: 12 },
    ];

    // Title
    row = ws.addRow(['Test Result Details']);
    applyStyle(row.getCell(1), styles.title);
    ws.addRow([]);

    row = ws.addRow(['Test Information']);
    applyStyle(row.getCell(1), styles.label);

    ws.addRow(['Regression Test ID:', test.regression_test_id ?? regressionId]);
    ws.addRow(['Flow Name:', test.flow_name ?? test.flowName ?? '']);
    ws.addRow(['Test Case:', test.test_case_name ?? test.test_case_id ?? test.testCase ?? '']);
    ws.addRow(['Market:', test.market ?? test.market_name ?? '']);
    ws.addRow(['Device Name:', test.device_name ?? test.device ?? '']);
    ws.addRow(['Device ID:', test.device_id_1 ?? test.device_id ?? test.deviceId ?? '']);
    ws.addRow(['Device Type:', test.device_type ?? test.deviceType ?? '']);
    ws.addRow(['User Email:', test.user_email ?? test.tester_email ?? '']);
    ws.addRow([
      'Executed At:',
      formatTimestamp(test.created_at ?? test.executed_at ?? test.timestamp_start),
    ]);
    ws.addRow(['Test Start:', formatTimestamp(test.start_time ?? test.timestamp_start)]);
    ws.addRow(['Test End:', formatTimestamp(test.end_time ?? test.timestamp_end)]);
    ws.addRow(['Notes:', test.notes ?? '']);
    ws.addRow([]);

    row = ws.addRow(['Validation Summary']);
    applyStyle(row.getCell(1), styles.label);

    const s = test.summary || {};
    const totalEventsTest =
      s.total_events ?? s.totalEvents ?? test.total_events ?? test.totalEvents ?? 0;
    const passedEventsTest = s.passed ?? s.passed_events ?? test.passed_events ?? test.passed ?? 0;
    const failedEventsTest = s.failed ?? s.failed_events ?? test.failed_events ?? test.failed ?? 0;
    const missingEventsTest =
      s.missing ?? s.missing_events ?? test.missing_events ?? test.missing ?? 0;
    const nonValidatedTest =
      s.non_validated ?? s.non_validated_events ?? test.non_validated_events ?? 0;
    const notPresentInAppTest = s.not_present_in_app ?? test.not_present_in_app ?? 0;
    const orderIssuesTest = s.order_issues ?? test.order_issues ?? 0;
    const passRateTest = s.pass_rate ?? test.pass_rate ?? test.passRate ?? 0;

    ws.addRow(['Total Events:', totalEventsTest]);
    ws.addRow(['Passed Events:', passedEventsTest]);
    ws.addRow(['Failed Events:', failedEventsTest]);
    ws.addRow(['Missing Events:', missingEventsTest]);
    ws.addRow(['Non-Validated Events:', nonValidatedTest]);
    ws.addRow(['Not Present In App:', notPresentInAppTest]);
    ws.addRow(['Order Issues:', orderIssuesTest]);
    ws.addRow(['Pass Rate:', `${passRateTest}%`]);
    ws.addRow([]);

    row = ws.addRow(['Event Details']);
    applyStyle(row.getCell(1), styles.eventDetailsHeader);
    ws.addRow([]);

    // Events
    const rawEvents =
      safeGet(test, ['validation_state', 'output'], null) ??
      safeGet(test, ['validation_state'], null) ??
      safeGet(test, ['output'], null) ??
      safeGet(test, ['events'], null) ??
      [];

    const eventsArr = Array.isArray(rawEvents) ? rawEvents : [];

    // Filter out non-validated
    const eventsToInclude = eventsArr.filter(Boolean);

    eventsToInclude.forEach((ev, eIdx) => {
      const eventNum = eIdx + 1;
      const eventTitle = ev.stepName || ev.eventName || ev.beaconID || `Event ${eventNum}`;

      row = ws.addRow([`Event ${eventNum}: ${eventTitle}`]);
      applyStyle(row.getCell(1), styles.eventTitle);
      ws.addRow([]);

      ws.addRow(['Event Name:', ev.eventName ?? ev.stepName ?? ev.beaconID ?? '']);
      const eventType =
        ev.beaconType === 'inbound'
          ? 'Inbound'
          : ev.beaconType
            ? `Outbound (${ev.beaconType})`
            : '';
      ws.addRow(['Event Type:', eventType]);
      const timestamp = ev.isMissing || ev.is_missing ? 'Missing' : formatTimestamp(ev.timestamp);
      ws.addRow(['Timestamp:', timestamp]);
      ws.addRow(['Expected Sequence:', ev.stepNumber ?? ev.expectedSequence ?? '']);
      ws.addRow(['Actual Sequence:', ev.actualSequence ?? ev.actual_sequence ?? '']);

      const statusObj = determineEventStatus(ev);
      row = ws.addRow(['Status:', statusObj.text ?? '']);
      applyStyle(row.getCell(2), statusObj.style ?? {});
      ws.addRow([]);

      // Attributes
      const attrRows = buildAttributeRows(ev);
      if (attrRows.length) {
        const attrHeaderRow = ws.addRow([
          'Attribute Name',
          'Expected Value',
          'Actual Value',
          'Status',
          'Type',
        ]);
        attrHeaderRow.eachCell((cell) => applyStyle(cell, styles.header));

        attrRows.forEach((r) => {
          const dataRow = ws.addRow([
            r.varName,
            r.expected,
            r.actual,
            r.displayStatus,
            r.fieldType,
          ]);
          if (r.isStatic) {
            applyStyle(dataRow.getCell(1), styles.staticField);
          }
          applyStyle(dataRow.getCell(4), r.style);
        });
        ws.addRow([]);
      } else {
        const attrHeaderRow = ws.addRow(['Attribute Name', 'Expected Value', 'Status']);
        attrHeaderRow.eachCell((cell) => applyStyle(cell, styles.header));

        if (ev.isMissing || ev.is_missing) {
          const truthData = safeGet(ev, ['truth', 'data'], {}) || {};
          Object.entries(truthData).forEach(([k, v]) => {
            const expectedVal = v && v.value !== undefined ? v.value : v;
            const dataRow = ws.addRow([k, expectedVal, 'Missing']);
            applyStyle(dataRow.getCell(1), styles.staticField);
            applyStyle(dataRow.getCell(3), styles.missing);
          });
        }
        ws.addRow([]);
      }
    });

    if (eventsToInclude.length === 0) {
      ws.addRow(['No validated events available (all events non-validated or missing).']);
    }
  });

  // Generate file
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const ts = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);

  const filename = `Test_History_${regressionId}_${ts}.xlsx`;

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}
